import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { mutate } from "swr";
import { CheckboxIcon, VerticalDotsIcon } from "@aureskonnect/react-ui";
import { isExists } from "../VatRates/ActionColumnVatRate";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";

import ErrorToast from "@components/Common/ErrorTost";
import {
    storeVatRate,
    setUrlHistory,
    setIsAddVatRateOriginHistoryClicked,
    setListLinks,
    setDataIsUpdatedFranchise,
    setConfirmationActivateClick,
    setFnDelete,
    setIsDeleteCheckClick,
    setActions,
    setIsActionsClicked,
    setIsEdited,
} from "../store";
import { store } from "../../../../components/VerticalLayout/store";
import { useFetchGlobal } from "../../../../api/hooks";
import DropdownItem from "./DropItem";

interface ActionProps {
    selectedIds: string[];
    userID: number;
    franchiseID: string;
    selectedRows: any[];
    franchise: boolean;
    typeDisplay: string;
    setTypeDisplay: (type: string) => void;
    getInfoNumberVatRate: () => void;
    setIsAddVatRateClicked: (clicked: boolean) => void;
    setLimit: (limit: number) => void;
}

const API_BASE = process.env.REACT_APP_API_V2_URL;

export default function DropdownActionList({
    selectedIds = [],
    userID,
    franchiseID,
    selectedRows,
    franchise,
    typeDisplay,
    setTypeDisplay,
    getInfoNumberVatRate,
    setIsAddVatRateClicked,
    setLimit,
}: ActionProps) {
    const { fetchGlobal } = useFetchGlobal();
    const { t } = useTranslation();
    const { oneShop, shopID, operatorID } = useSnapshot(store);
    const {
        isAddVatRateOriginHistoryClicked,
        ListNumberOrigin,
        dataShops,
        dataFranchise,
        isEdited,
        isActionsClicked,
        modify,
    } = useSnapshot(storeVatRate);

    const uuidUser = localStorage.getItem("uuidUser");
    const isArchiving = typeDisplay === "notArchived";

    const [canUnarchiveWithShop, setCanUnarchiveWithShop] = useState(false);

    useEffect(() => {
        const hasArchivedItems = dataFranchise?.some(
            (item: any) => selectedIds?.includes(item?.Id) && item?.archived
        );
        setCanUnarchiveWithShop(hasArchivedItems && oneShop && !isArchiving);
    }, [selectedIds, dataFranchise, oneShop, isArchiving]);

    const headers = useMemo(
        () => ({
            "Content-Type": "application/json",
        }),
        []
    );

    const buildArchiveUrl = useCallback(
        (id: string, shopId?: string) => {
            const endpoint = franchise
                ? "vatRateArchivedEdit"
                : "vatRateArchiveAssocEdit";
            const shopParam = franchise ? "" : `&shopId=${shopId || shopID}`;
            return `${API_BASE}/settings/general/${endpoint}?userId=${userID}&archived=${isArchiving}&franchiseId=${franchiseID}&idVatRate=${id}${shopParam}&operatorId=${operatorID}&uuidUser=${uuidUser}`;
        },
        [
            franchise,
            isArchiving,
            userID,
            franchiseID,
            shopID,
            operatorID,
            uuidUser,
        ]
    );

    const showSuccessToast = useCallback((message: string) => {
        toast.success(message, {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
            theme: "colored",
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            icon: <CheckboxIcon height={25} width={25} fill="white" />,
        });
    }, []);

    const executeArchiveRequests = useCallback(
        async (links: string[], successMessage: string) => {
            for (let i = 0; i < links.length; i++) {
                const data: any = await fetchGlobal(links[i], {
                    headers,
                    method: "DELETE",
                });
                await mutate(links[i], data);
                if (data.error)
                    throw new Error("Error while delete selection!");
                if (i === links.length - 1) {
                    setDataIsUpdatedFranchise(true);
                    showSuccessToast(successMessage);
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [headers, showSuccessToast]
    );

    const findAffectedIds = useCallback(
        (ids: string[]) => {
            const otherSiteArray: string[] = [];
            const shopArchivedArray: { Id: string; shopId: string }[] = [];

            if (franchise || oneShop) {
                dataShops.forEach((shop: any) => {
                    shop.subRows?.forEach((row: any) => {
                        ids.forEach((id) => {
                            if (row.Id === id) {
                                if (isArchiving && !row.archived && !oneShop) {
                                    otherSiteArray.push(id);
                                } else if (!isArchiving && row.archived) {
                                    shopArchivedArray.push({
                                        Id: row.Id,
                                        shopId: row.shopId,
                                    });
                                }
                            }
                        });
                    });
                });
            } else {
                dataFranchise.forEach((item: any) => {
                    if (
                        ids.includes(item.id) &&
                        item.archived === !isArchiving
                    ) {
                        otherSiteArray.push(item.id);
                    }
                });
            }

            return { otherSiteArray, shopArchivedArray };
        },
        [franchise, oneShop, dataShops, dataFranchise, isArchiving]
    );

    const archiveVatRate = useCallback(
        async (links: string[], ids: string[], isSomeFavorite = false) => {
            setLimit(2);
            const message = isArchiving
                ? t("The vat rate is successfully archived")
                : t("The vat rate is successfully restored");

            const { otherSiteArray, shopArchivedArray } = findAffectedIds(ids);

            if (isExists(otherSiteArray, ids) !== -1 && !oneShop) {
                ErrorToast(
                    t(
                        "Please note that you cannot archive an item assigned to stores."
                    )
                );
                return;
            }

            if (shopArchivedArray.length > 0 && franchise) {
                const additionalLinks = shopArchivedArray.map(
                    ({ Id, shopId }) =>
                        `${API_BASE}/settings/general/vatRateArchiveAssocEdit?userId=${userID}&archived=false&franchiseId=${franchiseID}&idVatRate=${Id}&shopId=${shopId}&operatorId=${operatorID}&uuidUser=${uuidUser}`
                );
                setListLinks([...links, ...additionalLinks]);
                setConfirmationActivateClick(true);
                return;
            }

            try {
                if (isArchiving) {
                    setFnDelete(async () => {
                        await executeArchiveRequests(links, message);
                        if (isSomeFavorite) {
                            ErrorToast(
                                t(
                                    "Please note, you cannot archive a favorite tva"
                                )
                            );
                        }
                    });
                    setIsDeleteCheckClick(true);
                } else {
                    await executeArchiveRequests(links, message);
                }
            } catch (e) {
                console.error("Archive error:", e);
            }
        },
        [
            isArchiving,
            findAffectedIds,
            oneShop,
            franchise,
            executeArchiveRequests,
            t,
            userID,
            franchiseID,
            operatorID,
            uuidUser,
            setLimit,
        ]
    );

    const handleTypeChange = useCallback(
        (newType: string) => {
            if (isEdited) {
                setIsActionsClicked(!isActionsClicked);
                setActions(() => {
                    setIsEdited(false);
                    setTypeDisplay(newType);
                    setIsAddVatRateClicked(false);
                });
            } else {
                setTypeDisplay(newType);
            }
        },
        [isEdited, isActionsClicked, setTypeDisplay, setIsAddVatRateClicked]
    );

    const handleHistory = useCallback(() => {
        const action = () => {
            const str = selectedIds.join(",");
            setUrlHistory(
                `${API_BASE}/settings/general/vatRate/origin_history?userId=${userID}&franchiseId=${franchiseID}&typeDisplay=all&idVatRates=${str}`
            );

            if (selectedIds.length === 1) {
                ErrorToast(
                    t(
                        "Attention, it is a multiple action. Please select at least two items"
                    )
                );
            } else {
                setIsAddVatRateOriginHistoryClicked(
                    !isAddVatRateOriginHistoryClicked
                );
            }
        };

        if (isEdited) {
            setIsActionsClicked(!isActionsClicked);
            setActions(() => {
                setIsEdited(false);
                action();
                setIsAddVatRateClicked(false);
            });
        } else {
            action();
        }
    }, [
        selectedIds,
        isEdited,
        isActionsClicked,
        isAddVatRateOriginHistoryClicked,
        userID,
        franchiseID,
        t,
        setIsAddVatRateClicked,
    ]);

    const handleArchiveAction = useCallback(async () => {
        setIsAddVatRateClicked(false);

        const processArchive = async () => {
            if (selectedIds.length <= 1) {
                ErrorToast(
                    t(
                        "Attention, it is a multiple action. Please select at least two items"
                    )
                );
                return;
            }

            const links: string[] = [];
            const ids: string[] = [];
            let hasFavorite = false;

            selectedRows.forEach((row) => {
                if (row.isFavorite && isArchiving) {
                    hasFavorite = true;
                } else if (!row[t("Shop")]) {
                    ids.push(row.Id);
                    links.push(buildArchiveUrl(row.Id));
                }
            });

            setListLinks(links);
            await archiveVatRate(links, ids, hasFavorite);
        };

        if (isEdited) {
            setIsActionsClicked(!isActionsClicked);
            setActions(async () => {
                setIsEdited(false);
                setIsAddVatRateClicked(false);
                await processArchive();
            });
        } else {
            await processArchive();
        }
    }, [
        selectedIds,
        selectedRows,
        isEdited,
        isActionsClicked,
        isArchiving,
        buildArchiveUrl,
        archiveVatRate,
        t,
        setIsAddVatRateClicked,
    ]);

    const isDisabled = !selectedIds?.length;
    const isArchiveDisabled = isDisabled || canUnarchiveWithShop;

    return (
        <div
            className={`cmn_drp_FRmenu dropdown`}
            onClick={getInfoNumberVatRate}
        >
            <VerticalDotsIcon
                id="dropdownMenuButton1"
                data-bs-toggle="dropdown"
                style={{ cursor: "pointer" }}
                height={25}
                width={25}
                fill="black"
            />
            <div className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                <DropdownItem
                    className="cmn_btn_FRlistActivated"
                    onClick={() => handleTypeChange("notArchived")}
                    label={`${t("Activated VAT rate(s)")} (${
                        ListNumberOrigin.nbrNotArchived
                    })`}
                />
                <DropdownItem
                    className="cmn_btn_FRlistArchived"
                    onClick={() => handleTypeChange("archived")}
                    label={`${t("Archived vat rate(s)")} (${
                        ListNumberOrigin.nbrArchived
                    })`}
                />
                <DropdownItem
                    className="cmn_btn_FRhistory"
                    onClick={handleHistory}
                    disabled={isDisabled}
                    label={t("Historical")}
                />
                {modify && (
                    <DropdownItem
                        className="cmn_btn_FRarchived cmn_btn_FRunarchive"
                        onClick={handleArchiveAction}
                        disabled={isArchiveDisabled}
                        label={isArchiving ? t("Archive") : t("Dearchive")}
                    />
                )}
            </div>
        </div>
    );
}
